import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, MapPin, Loader2, CreditCard, AlertCircle, Truck, Gift, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export const LocationPayment = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [location, setLocation] = useState(null);
  const [includeAddon, setIncludeAddon] = useState(false);

  const isPreviouslyRegistered = user?.previously_registered === true;

  useEffect(() => {
    fetchLocationDetails();
  }, [locationId]);

  const fetchLocationDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const loc = response.data.find(l => l.id === locationId);
      if (loc) {
        setLocation(loc);
      } else {
        toast.error('Standort nicht gefunden');
        navigate('/admin/locations');
      }
    } catch (error) {
      console.error('Error fetching location:', error);
      toast.error('Fehler beim Laden der Standort-Details');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setPaying(true);
    try {
      const token = localStorage.getItem('token');
      const frontend_url = window.location.origin;
      
      const response = await axios.post(
        `${API}/api/admin/locations/${locationId}/pay`,
        { frontend_url, include_addon: includeAddon },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating payment session:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen der Zahlungssitzung');
      setPaying(false);
    }
  };

  const handlePayLater = () => {
    toast.info('Sie können den Standort später unter "Standorte" bezahlen');
    navigate('/admin/locations');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!location) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <p>Standort nicht gefunden</p>
        </div>
      </DashboardLayout>
    );
  }

  const netPrice = location.base_price || 14.99;
  const vatRate = 0.19;
  const vatAmount = netPrice * vatRate;
  const grossPrice = netPrice + vatAmount;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="space-y-2">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/locations')}
            className="mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück zu Standorten
          </Button>
          
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <CreditCard className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Standort aktivieren</h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                Bezahlen Sie jetzt, um diesen Standort zu nutzen
              </p>
            </div>
          </div>
        </div>

        {/* Info Alert */}
        <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900 dark:text-blue-100">
            <p className="font-semibold mb-1">Wichtiger Hinweis</p>
            <p>
              Dieser Standort ist derzeit nicht aktiv. Nach erfolgreicher Zahlung können Sie 
              sofort Module aktivieren und den Standort nutzen.
            </p>
          </div>
        </div>

        {/* Location Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Standort Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Name</p>
                <p className="font-medium">{location.name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Position</p>
                <p className="font-medium">{location.location_position || 'N/A'}. Standort</p>
              </div>
              {location.address && (
                <div className="sm:col-span-2">
                  <p className="text-sm text-muted-foreground">Adresse</p>
                  <p className="font-medium">{location.address}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pricing */}
        <Card>
          <CardHeader>
            <CardTitle>💶 Preisübersicht</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Basis-Preis</span>
                <span className="font-medium">{netPrice.toFixed(2)}€/Monat</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">MwSt. (19%)</span>
                <span className="font-medium">{vatAmount.toFixed(2)}€</span>
              </div>
              <div className="h-px bg-border my-2"></div>
              <div className="flex justify-between text-lg font-bold">
                <span>Gesamt</span>
                <span className="text-primary">{grossPrice.toFixed(2)}€/Monat</span>
              </div>
            </div>

            {!isPreviouslyRegistered && (
              <div className="p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-900 dark:text-blue-100">
                  ✅ <span className="font-semibold">Starter: 29,90 € Netto / 35,58 € Brutto / Monat</span>
                  <br />
                  15 Mitarbeiter inklusive · Monatlich kündbar
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Lieferschein-KI-Manager Add-on */}
        <Card
          className={`cursor-pointer transition-all duration-200 border-2 ${includeAddon ? 'border-blue-400 bg-blue-50/40 dark:bg-blue-950/10' : 'border-dashed border-blue-200 hover:border-blue-300'}`}
          onClick={() => setIncludeAddon(!includeAddon)}
          data-testid="addon-checkbox-card"
        >
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${includeAddon ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                {includeAddon && <Check className="h-3 w-3 text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <Truck className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <span className="font-semibold text-sm">Lieferschein-KI-Manager dazu buchen</span>
                  {!isPreviouslyRegistered && (
                    <span className="inline-flex items-center gap-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs px-2 py-0.5 rounded-full">
                      <Gift className="h-3 w-3" /> 6 Monate GRATIS
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  KI-Import via E-Mail · papierlos · rechtssicher archiviert
                  <br />
                  {isPreviouslyRegistered
                    ? <span className="font-medium text-blue-700 dark:text-blue-400">1,99&nbsp;€/Monat · Jederzeit kündbar</span>
                    : <span className="font-medium text-blue-700 dark:text-blue-400">Nach 6 Monaten: 1,99&nbsp;€/Monat · Jederzeit kündbar</span>
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handlePayment}
            disabled={paying}
            className="flex-1"
            size="lg"
            data-testid="location-pay-btn"
          >
            {paying ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Weiterleitung...
              </>
            ) : (
              <>
                <CreditCard className="h-5 w-5 mr-2" />
                {includeAddon ? 'Jetzt buchen (inkl. Add-on)' : 'Jetzt sicher bezahlen'}
              </>
            )}
          </Button>
          
          <Button
            variant="outline"
            onClick={handlePayLater}
            disabled={paying}
            className="flex-1"
            size="lg"
          >
            Später bezahlen
          </Button>
        </div>

        {/* Security Note */}
        <p className="text-xs text-center text-muted-foreground">
          🔒 Sichere Zahlung über Stripe. Ihre Zahlungsinformationen werden verschlüsselt übertragen.
        </p>
      </div>
    </DashboardLayout>
  );
};
